import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env?.VITE_SUPABASE_ANON_KEY;

// Helper function to validate configuration
const isValidConfig = () => {
  return !!(
    supabaseUrl && 
    supabaseAnonKey && 
    supabaseUrl?.startsWith('https://') &&
    supabaseAnonKey?.startsWith('eyJ') &&
    supabaseAnonKey !== 'None'&& !supabaseAnonKey?.includes('your-supabase-anon-key-here')
  );
};

// Create a proper fallback query builder that prevents "eq is not a function" errors
const createFallbackQueryBuilder = () => {
  const mockData = [];
  
  const queryBuilder = {
    select: (columns = '*') => queryBuilder,
    insert: (data) => queryBuilder,
    update: (data) => queryBuilder,
    delete: () => queryBuilder,
    eq: (column, value) => queryBuilder,
    neq: (column, value) => queryBuilder,
    gt: (column, value) => queryBuilder,
    gte: (column, value) => queryBuilder,
    lt: (column, value) => queryBuilder,
    lte: (column, value) => queryBuilder,
    like: (column, pattern) => queryBuilder,
    ilike: (column, pattern) => queryBuilder,
    in: (column, values) => queryBuilder,
    is: (column, value) => queryBuilder,
    order: (column, options) => queryBuilder,
    limit: (count) => queryBuilder,
    single: () => Promise.resolve({ data: null, error: null }),
    maybeSingle: () => Promise.resolve({ data: null, error: null }),
    then: (resolve) => resolve({ data: mockData, error: null }),
    catch: (reject) => queryBuilder
  };
  
  return queryBuilder;
};

// Create Supabase client with improved error handling
let supabase = null;
let isSupabaseAvailable = false;

try {
  if (isValidConfig()) {
    supabase = createClient(supabaseUrl, supabaseAnonKey, {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
      },
      realtime: {
        channels: {},
        endpoint: `${supabaseUrl?.replace('https://', 'wss://')}/realtime/v1`,
      }
    });
    
    isSupabaseAvailable = true;
    console.log('✅ Supabase client initialized successfully');
  } else {
    console.warn('⚠️  Supabase not configured - using offline mode with enhanced fallback data');
    isSupabaseAvailable = false;
    
    // Create a comprehensive fallback client that prevents runtime errors
    supabase = {
      from: (table) => createFallbackQueryBuilder(),
      rpc: (functionName, params) => Promise.resolve({ 
        data: null, 
        error: { message: 'Database functions unavailable in offline mode' } 
      }),
      auth: {
        signUp: () => Promise.resolve({ 
          data: null, 
          error: { message: 'Authentication unavailable in offline mode' } 
        }),
        signInWithPassword: () => Promise.resolve({ 
          data: null, 
          error: { message: 'Authentication unavailable in offline mode' } 
        }),
        signOut: () => Promise.resolve({ error: null }),
        getSession: () => Promise.resolve({ data: { session: null }, error: null }),
        getUser: () => Promise.resolve({ data: { user: null }, error: null }),
        onAuthStateChange: (callback) => ({ 
          data: { 
            subscription: { 
              unsubscribe: () => console.log('Mock auth unsubscribed') 
            } 
          } 
        })
      },
      functions: {
        invoke: (functionName, options) => Promise.resolve({ 
          data: null, 
          error: { message: 'Edge functions unavailable in offline mode' } 
        })
      },
      storage: {
        from: (bucket) => ({
          upload: () => Promise.resolve({ 
            data: null, 
            error: { message: 'Storage unavailable in offline mode' } 
          }),
          download: () => Promise.resolve({ 
            data: null, 
            error: { message: 'Storage unavailable in offline mode' } 
          }),
          list: () => Promise.resolve({ 
            data: [], 
            error: { message: 'Storage unavailable in offline mode' } 
          })
        })
      }
    };
  }
} catch (error) {
  console.error('Failed to initialize Supabase client:', error);
  isSupabaseAvailable = false;
  supabase = {
    from: (table) => createFallbackQueryBuilder(),
    rpc: () => Promise.resolve({ data: null, error: { message: 'Database unavailable' } }),
    auth: {
      signUp: () => Promise.resolve({ data: null, error: { message: 'Authentication unavailable' } }),
      signInWithPassword: () => Promise.resolve({ data: null, error: { message: 'Authentication unavailable' } }),
      signOut: () => Promise.resolve({ error: null }),
      getSession: () => Promise.resolve({ data: { session: null }, error: null }),
      getUser: () => Promise.resolve({ data: { user: null }, error: null }),
      onAuthStateChange: () => ({ data: { subscription: { unsubscribe: () => {} } } })
    }
  };
}

export { supabase, isSupabaseAvailable };

// Types for better TypeScript support
export const SUBSCRIPTION_TIERS = {
  FREE: 'free',
  PRO: 'pro',
  ENTERPRISE: 'enterprise'
};

export const SUBSCRIPTION_STATUS = {
  ACTIVE: 'active',
  INACTIVE: 'inactive',
  CANCELLED: 'cancelled',
  PAST_DUE: 'past_due'
};

// Helper functions
export const getSupabaseStatus = () => {
  if (isSupabaseAvailable) {
    return { 
      configured: true, 
      message: 'Supabase is properly configured and available',
      mode: 'online' 
    };
  } else {
    return { 
      configured: false, 
      message: 'Running in offline mode with enhanced fallback data - configure VITE_SUPABASE_ANON_KEY for full functionality',
      mode: 'offline',
      details: 'Set a valid Supabase anonymous key in your environment variables'
    };
  }
};

export const isSupabaseConfigured = () => isSupabaseAvailable;

// Enhanced debugging helper
export const debugSupabaseConfig = () => {
  console.group('🔍 Supabase Configuration Debug');
  console.log('URL configured:', !!supabaseUrl);
  console.log('URL valid:', supabaseUrl?.startsWith('https://'));
  console.log('Anon key configured:', !!supabaseAnonKey);
  console.log('Anon key valid format:', supabaseAnonKey?.startsWith('eyJ'));
  console.log('Not default placeholder:', !supabaseAnonKey?.includes('your-supabase-anon-key-here'));
  console.log('Not "None":', supabaseAnonKey !== 'None');
  console.log('Is available:', isSupabaseAvailable);
  console.log('Current mode:', isSupabaseAvailable ? 'online' : 'offline');
  console.groupEnd();
};